/**
 * Canadian city → province code map for proactive typo detection on
 * the Realtor.ca by-name importer. Data-entry hedge: catches
 * "Edmonton, SK" before it costs an Apify call.
 *
 * Not exhaustive — we only need major metros + every city referenced
 * anywhere in the CRM (Locations entities, top-performer feeds, etc.).
 * Extend freely; no downside beyond a slightly bigger bundle.
 *
 * Keys are lower-cased for case-insensitive lookup.
 */
export const CITY_TO_PROV = {
  // Alberta
  "calgary": "AB", "edmonton": "AB", "red deer": "AB", "lethbridge": "AB",
  "medicine hat": "AB", "grande prairie": "AB", "airdrie": "AB",
  "spruce grove": "AB", "leduc": "AB", "fort mcmurray": "AB",
  "st. albert": "AB", "st albert": "AB", "wetaskiwin": "AB", "cold lake": "AB",
  "camrose": "AB", "lloydminster": "AB",
  // British Columbia
  "vancouver": "BC", "victoria": "BC", "kelowna": "BC", "surrey": "BC",
  "burnaby": "BC", "richmond": "BC", "abbotsford": "BC", "nanaimo": "BC",
  "prince george": "BC", "kamloops": "BC", "chilliwack": "BC",
  "coquitlam": "BC", "langley": "BC", "delta": "BC", "saanich": "BC",
  "north vancouver": "BC", "west vancouver": "BC",
  // Manitoba
  "winnipeg": "MB", "brandon": "MB", "steinbach": "MB", "portage la prairie": "MB",
  "thompson": "MB", "winkler": "MB", "selkirk": "MB",
  // New Brunswick
  "moncton": "NB", "saint john": "NB", "fredericton": "NB", "dieppe": "NB",
  "miramichi": "NB", "bathurst": "NB", "edmundston": "NB",
  // Newfoundland and Labrador
  "st. john's": "NL", "st johns": "NL", "mount pearl": "NL",
  "corner brook": "NL", "conception bay south": "NL", "paradise": "NL",
  // Nova Scotia
  "halifax": "NS", "sydney": "NS", "dartmouth": "NS", "truro": "NS",
  "new glasgow": "NS", "yarmouth": "NS", "kentville": "NS",
  // Ontario
  "toronto": "ON", "ottawa": "ON", "mississauga": "ON", "brampton": "ON",
  "hamilton": "ON", "london": "ON", "markham": "ON", "vaughan": "ON",
  "kitchener": "ON", "windsor": "ON", "oshawa": "ON", "burlington": "ON",
  "sudbury": "ON", "oakville": "ON", "barrie": "ON", "guelph": "ON",
  "cambridge": "ON", "whitby": "ON", "kingston": "ON", "st. catharines": "ON",
  "st catharines": "ON", "thunder bay": "ON", "waterloo": "ON",
  "chatham": "ON", "chatham-kent": "ON", "richmond hill": "ON",
  "pickering": "ON", "milton": "ON", "ajax": "ON", "peterborough": "ON",
  "kanata": "ON", "niagara falls": "ON", "sault ste. marie": "ON",
  "sault ste marie": "ON", "sarnia": "ON", "welland": "ON", "belleville": "ON",
  // Prince Edward Island
  "charlottetown": "PE", "summerside": "PE", "stratford": "PE",
  // Quebec
  "montreal": "QC", "montréal": "QC", "quebec city": "QC", "québec": "QC",
  "quebec": "QC", "laval": "QC", "gatineau": "QC", "longueuil": "QC",
  "sherbrooke": "QC", "levis": "QC", "lévis": "QC", "saguenay": "QC",
  "trois-rivières": "QC", "trois-rivieres": "QC", "terrebonne": "QC",
  "brossard": "QC", "drummondville": "QC", "saint-jérôme": "QC",
  "saint jerome": "QC", "granby": "QC", "blainville": "QC",
  // Saskatchewan
  "saskatoon": "SK", "regina": "SK", "prince albert": "SK", "moose jaw": "SK",
  "swift current": "SK", "yorkton": "SK", "north battleford": "SK",
  "warman": "SK", "estevan": "SK", "weyburn": "SK", "lloydminster sk": "SK",
  "melfort": "SK",
  // Territories
  "yellowknife": "NT", "whitehorse": "YT", "iqaluit": "NU",
};

/**
 * Look up the province code for a free-text city string. Returns "" if
 * the city isn't recognized (in which case we don't warn — no false
 * positives). Case-insensitive; strips trailing/leading whitespace.
 */
export function lookupProvForCity(city) {
  if (!city) return "";
  const key = city.trim().toLowerCase().replace(/\s+/g, " ");
  return CITY_TO_PROV[key] || "";
}

/**
 * Parse a free-text "City, PR" or "City PR" into { city, prov } (both
 * strings). Missing pieces come back as empty strings so callers can
 * decide what to do (e.g. don't warn when prov is blank).
 */
export function parseCityAndProv(input) {
  if (!input) return { city: "", prov: "" };
  const text = input.trim();
  if (!text) return { city: "", prov: "" };
  const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { city: parts[0], prov: parts[1].toUpperCase() };
  }
  const tokens = text.split(/\s+/);
  if (tokens.length >= 2 && /^[A-Za-z]{2}$/.test(tokens[tokens.length - 1])) {
    return {
      city: tokens.slice(0, -1).join(" "),
      prov: tokens[tokens.length - 1].toUpperCase(),
    };
  }
  return { city: text, prov: "" };
}

/**
 * Given a raw "City, PR" input, return the mismatch info (or null if OK):
 *   { city, typedProv, expectedProv }
 * "OK" means we couldn't infer a province, or the typed one matches.
 */
export function detectProvMismatch(input) {
  const { city, prov } = parseCityAndProv(input);
  if (!city || !prov) return null;
  const expected = lookupProvForCity(city);
  if (!expected) return null; // unknown city, no warning
  if (expected === prov) return null;
  return { city, typedProv: prov, expectedProv: expected };
}
